// src/components/Accessibility/AccessibilityToolbar.tsx
// SOLUTION AVEC PORTAL : Rendu hors du #root
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Eye, Type, Contrast, MousePointer, Pause, RotateCcw } from 'lucide-react';

interface AccessibilitySettings {
  fontSize: 'small' | 'normal' | 'large' | 'xlarge';
  contrast: 'normal' | 'high' | 'higher';
  cursorSize: 'normal' | 'large' | 'xlarge';
  animations: boolean;
}

const DEFAULT_SETTINGS: AccessibilitySettings = {
  fontSize: 'normal',
  contrast: 'normal',
  cursorSize: 'normal',
  animations: true
};

export default function AccessibilityToolbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState<AccessibilitySettings>(() => {
    try {
      const saved = localStorage.getItem('accessibility-settings');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Error loading settings:', e);
    }
    return DEFAULT_SETTINGS;
  });
  const [mounted] = useState(() => typeof document !== 'undefined');

  const applySettings = (newSettings: AccessibilitySettings) => {
    const root = document.documentElement;
    const body = document.body;

    // Taille de police
    const fontSizes = { small: '14px', normal: '16px', large: '18px', xlarge: '20px' };
    root.style.fontSize = fontSizes[newSettings.fontSize];

    // Contraste - appliqué sur #app-content (pas #root pour ne pas casser position:fixed de la navbar)
    const contentDiv = document.getElementById('app-content');
    if (contentDiv) {
      contentDiv.classList.remove('contrast-high', 'contrast-higher');
      if (newSettings.contrast !== 'normal') {
        contentDiv.classList.add(`contrast-${newSettings.contrast}`);
      }
    }

    // Appliquer aussi le contraste à la navbar (elle est hors du wrapper filtré)
    body.classList.remove('contrast-nav-high', 'contrast-nav-higher');
    if (newSettings.contrast !== 'normal') {
      body.classList.add(`contrast-nav-${newSettings.contrast}`);
    }

    // Curseur
    body.classList.remove('cursor-large', 'cursor-xlarge');
    if (newSettings.cursorSize !== 'normal') {
      body.classList.add(`cursor-${newSettings.cursorSize}`);
    }

    // Animations
    body.classList.toggle('reduce-motion', !newSettings.animations);
  };

  useEffect(() => {
    localStorage.setItem('accessibility-settings', JSON.stringify(settings));
    applySettings(settings);
  }, [settings]);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* CONTRASTE : Appliqué sur #app-content (pas #root, sinon casse position:fixed de la navbar) */
      #app-content.contrast-high {
        filter: contrast(1.3) brightness(1.05);
      }
      #app-content.contrast-higher {
        filter: contrast(1.6) brightness(1.1);
      }

      /* Appliquer le contraste aussi à la navbar (fixed, hors du wrapper filtré) */
      body.contrast-nav-high nav.fixed {
        filter: contrast(1.3) brightness(1.05);
      }
      body.contrast-nav-higher nav.fixed {
        filter: contrast(1.6) brightness(1.1);
      }
      
      /* REDUCE MOTION: Désactive toutes les animations */
      body.reduce-motion *,
      body.reduce-motion *::before,
      body.reduce-motion *::after {
        animation-duration: 0.01ms !important;
        transition-duration: 0.01ms !important;
        scroll-behavior: auto !important;
      }
      
      /* EXCEPTION: Éléments avec data-keep-animation gardent leurs animations */
      body.reduce-motion [data-keep-animation="true"],
      body.reduce-motion [data-keep-animation="true"]::before,
      body.reduce-motion [data-keep-animation="true"]::after {
        animation-duration: revert !important;
        transition-duration: revert !important;
      }
      
      body.cursor-large, body.cursor-large * {
        cursor: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32"><path d="M2 2 L2 28 L12 22 L16 28 L20 26 L16 20 L24 20 Z" fill="black" stroke="white"/></svg>') 0 0, auto !important;
      }
      body.cursor-xlarge, body.cursor-xlarge * {
        cursor: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48"><path d="M2 2 L2 42 L18 33 L24 42 L30 39 L24 30 L36 30 Z" fill="black" stroke="white"/></svg>') 0 0, auto !important;
      }
    `;
    document.head.appendChild(style);
    return (): void => { document.head.removeChild(style); };
  }, []);

  const contrastLabel = { normal: 'Normal', high: 'Élevé', higher: 'Max' };
  const cursorLabel = { normal: 'Normal', large: 'Grand', xlarge: 'XL' };

  // Contenu du composant
  const content = (
    <>
      {/* Bouton accessibilité */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed left-4 bottom-20 z-[60] w-12 h-12 bg-aurore-dark text-white rounded-full shadow-lg hover:bg-aurore-medium hover:scale-110 transition-all duration-300 flex items-center justify-center"
        aria-label="Accessibilité"
        data-keep-animation="true"
      >
        <Eye className="w-6 h-6" />
      </button>

      {/* Panel avec backdrop */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/20 z-[59]" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Panel */}
          <div 
            className="fixed left-4 bottom-36 z-[61] bg-white rounded-xl shadow-2xl p-4 w-72 border-2 border-aurore-dark"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-base font-bold">Accessibilité</h3>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                }}
                className="hover:text-gray-700 text-xl leading-none"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3">
              {/* Taille */}
              <div>
                <label className="block text-xs font-semibold mb-1">
                  <Type className="w-3 h-3 inline mr-1" />Taille
                </label>
                <div className="grid grid-cols-4 gap-1">
                  {(['small', 'normal', 'large', 'xlarge'] as const).map(size => (
                    <button
                      key={size}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSettings(prev => ({ ...prev, fontSize: size }));
                      }}
                      className={`px-1 py-1.5 rounded text-xs ${
                        settings.fontSize === size ? 'bg-aurore-dark text-white' : 'bg-gray-100'
                      }`}
                    >
                      {size[0].toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>

              {/* Contraste */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const order: AccessibilitySettings['contrast'][] = ['normal', 'high', 'higher'];
                  const idx = order.indexOf(settings.contrast);
                  const next = order[(idx + 1) % order.length];
                  setSettings(prev => ({ ...prev, contrast: next }));
                }}
                className="w-full px-3 py-2 bg-gray-100 hover:bg-aurore-light rounded flex justify-between items-center"
              >
                <span className="text-xs font-medium">
                  <Contrast className="w-3 h-3 inline mr-1" />Contraste: {contrastLabel[settings.contrast]}
                </span>
              </button>

              {/* Curseur */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const sizes: AccessibilitySettings['cursorSize'][] = ['normal', 'large', 'xlarge'];
                  const idx = sizes.indexOf(settings.cursorSize);
                  const next = sizes[(idx + 1) % sizes.length];
                  setSettings(prev => ({ ...prev, cursorSize: next }));
                }}
                className="w-full px-3 py-2 bg-gray-100 hover:bg-aurore-light rounded flex justify-between items-center"
              >
                <span className="text-xs font-medium">
                  <MousePointer className="w-3 h-3 inline mr-1" />Curseur: {cursorLabel[settings.cursorSize]}
                </span>
              </button>

              {/* Animations */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSettings(prev => ({ ...prev, animations: !prev.animations }));
                }}
                className="w-full px-3 py-2 bg-gray-100 hover:bg-aurore-light rounded flex justify-between items-center"
              >
                <span className="text-xs font-medium">
                  <Pause className="w-3 h-3 inline mr-1" />Animations
                </span>
                <div className={`w-10 h-5 rounded-full ${settings.animations ? 'bg-green-500' : 'bg-gray-300'}`}>
                  <div className={`w-4 h-4 bg-white rounded-full mt-0.5 transition-transform ${
                    settings.animations ? 'translate-x-5' : 'translate-x-0.5'
                  }`} />
                </div>
              </button>

              {/* Reset */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSettings(DEFAULT_SETTINGS);
                  localStorage.removeItem('accessibility-settings');
                }}
                className="w-full px-3 py-2 bg-aurore-dark text-white rounded hover:bg-aurore-medium flex justify-center items-center space-x-1 text-xs font-semibold"
              >
                <RotateCcw className="w-3 h-3" />
                <span>Réinitialiser</span>
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );

  // Rendre avec Portal HORS du #root, directement dans le body
  if (!mounted) return null;
  return createPortal(content, document.body);
}